/**
 * Re-parent a single EnhancedDeal (sales project) from one lead to another,
 * including deal-scoped artifacts and client portal slice migration.
 */

import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  type UpdateData,
  type DocumentData,
} from 'firebase/firestore';
import { db } from './firebase';
import { defaultClientLabelFromLead } from './projectName';
import type { Activity, ClientPortal, EnhancedDeal, Lead, PortalProject } from '../types';

const BATCH_LIMIT = 400;

export class TransferDealToLeadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TransferDealToLeadError';
  }
}

export interface TransferDealToLeadResult {
  dealId: string;
  sourceLeadId: string;
  targetLeadId: string;
  targetPortalId: string | null;
}

export interface TransferDealToLeadOptions {
  dealId: string;
  targetLeadId: string;
  userId: string;
  leadsCollectionName: string;
  createActivity?: (activity: Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
}

function leadDisplayName(lead: Lead): string {
  return `${lead.firstName} ${lead.lastName}`.trim();
}

async function batchUpdateQuery(
  collectionName: string,
  field: string,
  value: string,
  updates: UpdateData<DocumentData>,
): Promise<number> {
  const snap = await getDocs(query(collection(db, collectionName), where(field, '==', value)));
  if (snap.empty) return 0;

  let count = 0;
  let batch = writeBatch(db);
  let ops = 0;

  for (const d of snap.docs) {
    batch.update(d.ref, updates);
    ops += 1;
    count += 1;
    if (ops >= BATCH_LIMIT) {
      await batch.commit();
      batch = writeBatch(db);
      ops = 0;
    }
  }
  if (ops > 0) await batch.commit();
  return count;
}

async function batchUpdateByIds(
  refs: Array<ReturnType<typeof doc>>,
  updates: UpdateData<DocumentData>,
): Promise<void> {
  if (refs.length === 0) return;
  let batch = writeBatch(db);
  let ops = 0;
  for (const ref of refs) {
    batch.update(ref, updates);
    ops += 1;
    if (ops >= BATCH_LIMIT) {
      await batch.commit();
      batch = writeBatch(db);
      ops = 0;
    }
  }
  if (ops > 0) await batch.commit();
}

async function getTimberProjectIdsForDeal(dealId: string): Promise<string[]> {
  const snap = await getDocs(query(collection(db, 'timberProjects'), where('dealId', '==', dealId)));
  return snap.docs.map((d) => d.id);
}

/** Project Hub shows `clientLabel` / `clientName` from deal + timber project — sync after lead transfer. */
async function syncTimberProjectsClientForDeal(
  dealId: string,
  clientName: string,
  clientEmail: string,
): Promise<void> {
  const snap = await getDocs(query(collection(db, 'timberProjects'), where('dealId', '==', dealId)));
  if (snap.empty) return;

  let batch = writeBatch(db);
  let ops = 0;
  for (const d of snap.docs) {
    batch.update(d.ref, {
      clientName,
      clientEmail,
      updatedAt: Timestamp.now(),
    });
    ops += 1;
    if (ops >= BATCH_LIMIT) {
      await batch.commit();
      batch = writeBatch(db);
      ops = 0;
    }
  }
  if (ops > 0) await batch.commit();
}

function applyClientLabelToPortalSlice(
  slice: PortalProject | undefined,
  clientLabel: string,
): PortalProject | undefined {
  if (!slice) return undefined;
  return {
    ...slice,
    clientLabel: clientLabel || slice.clientLabel,
    updatedAt: new Date(),
  };
}

async function updateAnnotationsForProjectIds(
  projectIds: string[],
  targetLeadId: string,
): Promise<void> {
  for (const projectId of projectIds) {
    await batchUpdateQuery('pdfAnnotations', 'projectId', projectId, { leadId: targetLeadId });
  }
}

async function updateMarkupAndHighlightsForDocumentIds(
  documentIds: string[],
  targetLeadId: string,
): Promise<void> {
  if (documentIds.length === 0) return;
  const unique = Array.from(new Set(documentIds));
  for (const documentId of unique) {
    await batchUpdateQuery('documentMarkup', 'documentId', documentId, { leadId: targetLeadId });
    await batchUpdateQuery('csvCellHighlights', 'documentId', documentId, { leadId: targetLeadId });
    await batchUpdateQuery('pdfMeasurementCalibrations', 'documentId', documentId, { leadId: targetLeadId });
    await batchUpdateQuery('pdfMeasurements', 'documentId', documentId, { leadId: targetLeadId });
  }
}

async function findPortalDocByLeadId(leadId: string) {
  const snap = await getDocs(query(collection(db, 'clientPortals'), where('leadId', '==', leadId)));
  return snap.empty ? null : snap.docs[0];
}

async function createPortalForLead(lead: Lead, dealId: string, projectSlice?: PortalProject): Promise<string> {
  const lastName = lead.lastName.toLowerCase().replace(/[^a-z0-9]/g, '') || 'client';
  const portalKey = Math.floor(1000 + Math.random() * 9000).toString();
  const slug = `${lastName}-${portalKey}`;
  const accessPin = Math.floor(1000 + Math.random() * 9000).toString();
  const now = Timestamp.now();

  const projects: Record<string, PortalProject> = {};
  if (projectSlice) {
    projects[dealId] = projectSlice;
  } else {
    projects[dealId] = {
      dealId,
      projectName: '',
      currentStage: 'design-fee',
      stageHistory: [{ stage: 'design-fee', enteredAt: new Date() }],
      designFeeSkipped: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  }

  const portalDoc = {
    slug,
    accessPin,
    clientName: leadDisplayName(lead),
    clientEmail: lead.email,
    clientCompany: lead.company || '',
    leadId: lead.id,
    dealId,
    currentStage: 'design-fee',
    stageHistory: [{ stage: 'design-fee', enteredAt: now }],
    designFeeSkipped: false,
    modelStepSkipped: false,
    projects,
    createdAt: now,
    updatedAt: now,
  };

  const ref = await addDoc(collection(db, 'clientPortals'), portalDoc);
  return ref.id;
}

/**
 * Move `projects[dealId]` (and related uploads) from source lead's portal to target's.
 */
async function migratePortalSlice(
  dealId: string,
  sourceLeadId: string,
  targetLead: Lead,
  clientLabel: string,
): Promise<string | null> {
  const sourcePortalDoc = await findPortalDocByLeadId(sourceLeadId);
  let projectSlice: PortalProject | undefined;
  let uploadsToMove: ClientPortal['clientUploads'] = [];

  if (sourcePortalDoc) {
    const data = sourcePortalDoc.data() as ClientPortal & { projects?: Record<string, PortalProject> };
    projectSlice = data.projects?.[dealId] as PortalProject | undefined;

    if (!projectSlice && data.dealId === dealId) {
      projectSlice = {
        dealId,
        projectName: (data as { projectName?: string }).projectName || '',
        currentStage: data.currentStage || 'design-fee',
        stageHistory: data.stageHistory || [],
        designFeeSkipped: data.designFeeSkipped ?? false,
        engagementLetterId: data.engagementLetterId,
        engagementLetterSlug: data.engagementLetterSlug,
        proposalSlug: data.proposalSlug,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    }

    const uploads = data.clientUploads || [];
    uploadsToMove = uploads.filter((u) => u.dealId === dealId);
    const remainingUploads = uploads.filter((u) => u.dealId !== dealId);

    const sourceUpdates: UpdateData<DocumentData> = { updatedAt: Timestamp.now() };
    sourceUpdates[`projects.${dealId}`] = deleteField();

    if (data.dealId === dealId) {
      const otherDealIds = Object.keys(data.projects || {}).filter((id) => id !== dealId);
      if (otherDealIds.length > 0) {
        const nextPrimary = otherDealIds[0];
        const nextSlice = data.projects?.[nextPrimary];
        sourceUpdates.dealId = nextPrimary;
        if (nextSlice) {
          sourceUpdates.currentStage = nextSlice.currentStage;
          sourceUpdates.stageHistory = nextSlice.stageHistory;
        }
      } else {
        sourceUpdates.dealId = deleteField();
      }
    }

    if (uploadsToMove.length > 0 || uploads.length !== remainingUploads.length) {
      sourceUpdates.clientUploads = remainingUploads;
    }

    await updateDoc(sourcePortalDoc.ref, sourceUpdates);
  }

  projectSlice = applyClientLabelToPortalSlice(projectSlice, clientLabel);

  let targetPortalDoc = await findPortalDocByLeadId(targetLead.id!);
  let targetPortalId: string;

  if (!targetPortalDoc) {
    targetPortalId = await createPortalForLead(targetLead, dealId, projectSlice);
  } else {
    targetPortalId = targetPortalDoc.id;
    const targetUpdates: UpdateData<DocumentData> = { updatedAt: Timestamp.now() };
    if (projectSlice) {
      targetUpdates[`projects.${dealId}`] = projectSlice;
    } else if (!targetPortalDoc.data().projects?.[dealId]) {
      targetUpdates[`projects.${dealId}`] = {
        dealId,
        projectName: '',
        clientLabel: clientLabel || undefined,
        currentStage: 'design-fee',
        stageHistory: [{ stage: 'design-fee', enteredAt: Timestamp.now() }],
        designFeeSkipped: false,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
    }
    if (uploadsToMove.length > 0) {
      const existing = (targetPortalDoc.data().clientUploads || []) as ClientPortal['clientUploads'];
      targetUpdates.clientUploads = [...(existing || []), ...uploadsToMove];
    }
    if (!targetPortalDoc.data().dealId) {
      targetUpdates.dealId = dealId;
    }
    await updateDoc(targetPortalDoc.ref, targetUpdates);
  }

  if (sourcePortalDoc?.id) {
    const questionnaires = await getDocs(
      query(collection(db, 'designQuestionnaires'), where('portalId', '==', sourcePortalDoc.id)),
    );
    const qRefs = questionnaires.docs.map((d) => doc(db, 'designQuestionnaires', d.id));
    await batchUpdateByIds(qRefs, { leadId: targetLead.id!, portalId: targetPortalId });
  }

  return targetPortalId;
}

function reconcileLeadDealIds(dealIds: string[] | undefined, dealId: string, add: boolean): string[] {
  const list = dealIds || [];
  if (add) {
    return list.includes(dealId) ? list : [...list, dealId];
  }
  return list.filter((id) => id !== dealId);
}

function nextConvertedToDeal(dealIds: string[], removedDealId: string, current?: string): string | undefined {
  if (current && current !== removedDealId) return current;
  return dealIds[0];
}

export async function transferDealToLead(
  options: TransferDealToLeadOptions,
): Promise<TransferDealToLeadResult> {
  const { dealId, targetLeadId, userId, leadsCollectionName, createActivity } = options;

  const dealSnap = await getDoc(doc(db, 'enhancedDeals', dealId));
  if (!dealSnap.exists()) {
    throw new TransferDealToLeadError('Deal not found.');
  }
  const deal = { id: dealSnap.id, ...dealSnap.data() } as EnhancedDeal;
  const sourceLeadId = deal.leadId;
  if (!sourceLeadId) {
    throw new TransferDealToLeadError('This deal is not linked to a lead. Link it first, then transfer.');
  }
  if (sourceLeadId === targetLeadId) {
    throw new TransferDealToLeadError('Deal is already on this lead.');
  }

  const [sourceSnap, targetSnap] = await Promise.all([
    getDoc(doc(db, leadsCollectionName, sourceLeadId)),
    getDoc(doc(db, leadsCollectionName, targetLeadId)),
  ]);
  if (!sourceSnap.exists()) throw new TransferDealToLeadError('Source lead not found.');
  if (!targetSnap.exists()) throw new TransferDealToLeadError('Target lead not found.');

  const sourceLead = { id: sourceSnap.id, ...sourceSnap.data() } as Lead;
  const targetLead = { id: targetSnap.id, ...targetSnap.data() } as Lead;
  if (targetLead.archived) {
    throw new TransferDealToLeadError('Cannot transfer to an archived lead.');
  }
  if ((targetLead.dealIds || []).includes(dealId)) {
    throw new TransferDealToLeadError('This deal is already associated with the target lead.');
  }

  const contactName = leadDisplayName(targetLead);
  const clientLabel = defaultClientLabelFromLead(targetLead);
  const clientEmail = (targetLead.email || '').trim();

  const targetPortalId = await migratePortalSlice(dealId, sourceLeadId, targetLead, clientLabel);

  const dealUpdates: Partial<EnhancedDeal> = {
    leadId: targetLeadId,
    contactId: targetLead.id || deal.contactId,
    contactName,
    clientLabel: clientLabel || deal.clientLabel,
    company: targetLead.company ?? deal.company,
    location: targetLead.location || deal.location,
    updatedAt: new Date(),
  };

  await updateDoc(doc(db, 'enhancedDeals', dealId), {
    ...dealUpdates,
    updatedAt: Timestamp.now(),
  });

  await syncTimberProjectsClientForDeal(dealId, contactName, clientEmail);

  const sourceDealIds = reconcileLeadDealIds(sourceLead.dealIds, dealId, false);
  const sourceLeadPatch: UpdateData<DocumentData> = {
    dealIds: sourceDealIds,
    updatedAt: Timestamp.now(),
  };
  const nextPrimary = nextConvertedToDeal(sourceDealIds, dealId, sourceLead.convertedToDeal);
  if (nextPrimary) sourceLeadPatch.convertedToDeal = nextPrimary;
  else sourceLeadPatch.convertedToDeal = deleteField();

  await updateDoc(doc(db, leadsCollectionName, sourceLeadId), sourceLeadPatch);

  const targetDealIds = reconcileLeadDealIds(targetLead.dealIds, dealId, true);
  const targetLeadUpdates: UpdateData<DocumentData> = {
    dealIds: targetDealIds,
    updatedAt: Timestamp.now(),
  };
  if (!targetLead.convertedToDeal) {
    targetLeadUpdates.convertedToDeal = dealId;
  }
  await updateDoc(doc(db, leadsCollectionName, targetLeadId), targetLeadUpdates);

  const leadIdUpdate: UpdateData<DocumentData> = { leadId: targetLeadId };
  const targetLeadName = contactName;

  await batchUpdateQuery('engagementLetters', 'dealId', dealId, leadIdUpdate);
  await batchUpdateQuery('leadDocuments', 'dealId', dealId, leadIdUpdate);
  await batchUpdateQuery('leadTimeEntries', 'dealId', dealId, leadIdUpdate);
  await batchUpdateQuery('bids', 'dealId', dealId, leadIdUpdate);
  await batchUpdateQuery('proposals', 'dealId', dealId, leadIdUpdate);
  await batchUpdateQuery('pdfTakeoffPins', 'dealId', dealId, leadIdUpdate);
  await batchUpdateQuery('pdfTakeoffLinks', 'dealId', dealId, leadIdUpdate);
  await batchUpdateQuery('pdfTakeoffStructures', 'dealId', dealId, leadIdUpdate);

  const paymentsSnap = await getDocs(
    query(collection(db, 'payments'), where('dealId', '==', dealId)),
  );
  if (!paymentsSnap.empty) {
    const batch = writeBatch(db);
    paymentsSnap.docs.forEach((d) => {
      batch.update(d.ref, {
        leadId: targetLeadId,
        matchedLeadId: targetLeadId,
        updatedAt: Timestamp.now(),
      });
    });
    await batch.commit();
  }

  const timberProjectIds = await getTimberProjectIdsForDeal(dealId);
  await updateAnnotationsForProjectIds(timberProjectIds, targetLeadId);

  const leadDocSnap = await getDocs(
    query(collection(db, 'leadDocuments'), where('dealId', '==', dealId)),
  );
  const documentIds = leadDocSnap.docs.flatMap((d) => {
    const url = d.data().url as string | undefined;
    return [d.id, url].filter((x): x is string => !!x);
  });
  await updateMarkupAndHighlightsForDocumentIds(documentIds, targetLeadId);

  const proposalRef = doc(db, 'proposalBuilders', dealId);
  const proposalSnap = await getDoc(proposalRef);
  if (proposalSnap.exists()) {
    await updateDoc(proposalRef, {
      leadCompany: targetLead.company || '',
      clientName: contactName,
      location: targetLead.location || proposalSnap.data().location,
      updatedAt: Timestamp.now(),
    });
  }

  if (createActivity) {
    await createActivity({
      type: 'note',
      title: 'Project transferred to another lead',
      description: `Moved from ${leadDisplayName(sourceLead)} to ${targetLeadName}.`,
      entityType: 'deal',
      entityId: dealId,
      entityName: deal.title,
      createdBy: userId,
      status: 'completed',
      completedAt: new Date(),
    });
  }

  return {
    dealId,
    sourceLeadId,
    targetLeadId,
    targetPortalId,
  };
}

/** Pre-flight checks for UI warnings (does not mutate). */
export async function getTransferDealWarnings(
  deal: EnhancedDeal,
  targetLead: Lead,
  timberProjectCount: number,
): Promise<string[]> {
  const warnings: string[] = [];
  if (timberProjectCount > 0) {
    warnings.push('This deal has Project Hub timber project(s); schedule and tasks stay on the same project records.');
  }
  if (deal.outcome === 'won') {
    warnings.push('This deal is marked won.');
  }
  const proposalSnap = await getDoc(doc(db, 'proposalBuilders', deal.id!));
  if (proposalSnap.exists()) {
    const status = proposalSnap.data().status as string | undefined;
    if (status === 'approved') {
      warnings.push('This deal has a signed proposal.');
    }
  }
  const dealEmail = (deal as EnhancedDeal & { clientEmail?: string }).clientEmail;
  const targetEmail = targetLead.email?.toLowerCase().trim();
  const sourceName = deal.contactName?.toLowerCase().trim();
  const targetName = `${targetLead.firstName} ${targetLead.lastName}`.toLowerCase().trim();
  if (targetEmail && dealEmail && targetEmail !== dealEmail.toLowerCase().trim()) {
    warnings.push('Target lead email differs from the deal contact email.');
  }
  if (sourceName && targetName && sourceName !== targetName) {
    warnings.push('Target lead name differs from the deal contact name.');
  }
  return warnings;
}
